import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ==================== ENHANCED USER DASHBOARD ====================
export const EnhancedDashboard = ({ userId, onStartExam, onBack }) => {
  const [performance, setPerformance] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [perfRes, readyRes, subjectsRes] = await Promise.all([
          axios.get(`${API}/adaptive/performance/${userId}`),
          axios.get(`${API}/adaptive/readiness/${userId}`),
          axios.get(`${API}/adaptive/subjects`)
        ]);
        setPerformance(perfRes.data);
        setReadiness(readyRes.data);
        setSubjects(subjectsRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      }
      setLoading(false);
    };
    
    fetchData();
  }, [userId]);

  const hasData = performance && performance.total_questions_answered > 0;

  // Calculate subject stats for heat map
  const subjectStats = useMemo(() => {
    if (!performance?.subjects) return [];
    return Object.entries(performance.subjects)
      .map(([name, data]) => ({
        name,
        accuracy: data.accuracy || 0,
        total: data.total || 0,
        level: data.level || 2
      }))
      .sort((a, b) => a.accuracy - b.accuracy);
  }, [performance]);

  // Get color based on accuracy
  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'bg-green-500';
    if (accuracy >= 60) return 'bg-yellow-500';
    if (accuracy >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getAccuracyBg = (accuracy) => {
    if (accuracy >= 80) return 'bg-green-500/20 border-green-500/30';
    if (accuracy >= 60) return 'bg-yellow-500/20 border-yellow-500/30';
    if (accuracy >= 40) return 'bg-orange-500/20 border-orange-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" role="status" aria-live="polite">
        <div className="text-center">
          <div className="animate-spin text-6xl mb-4" aria-hidden="true">📊</div>
          <p className="text-white text-xl">טוען נתונים...</p>
          <span className="sr-only">טוען את לוח הבקרה</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" dir="rtl">
      {/* Skip to main content for accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:right-4 bg-purple-600 text-white px-4 py-2 rounded-lg z-50">
        דלג לתוכן הראשי
      </a>

      {/* Header */}
      <header className="bg-slate-800/80 backdrop-blur-lg border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={onBack} 
              className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-purple-500 rounded-lg px-3 py-2"
              aria-label="חזרה לדף הבית"
            >
              <span aria-hidden="true">←</span>
              <span>חזרה</span>
            </button>
            <h1 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
              <span aria-hidden="true">📊</span>
              לוח הבקרה שלי
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {!hasData ? (
          /* Empty State */
          <section className="text-center py-20" aria-labelledby="empty-state-title">
            <div className="text-8xl mb-6" aria-hidden="true">📚</div>
            <h2 id="empty-state-title" className="text-2xl font-bold text-white mb-4">אין נתונים עדיין</h2>
            <p className="text-slate-400 mb-8">התחל לתרגל כדי לראות את הסטטיסטיקות שלך</p>
            <button
              onClick={() => onStartExam('adaptive')}
              className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-8 py-4 rounded-xl font-bold text-lg focus:outline-none focus:ring-4 focus:ring-purple-500/50 hover:shadow-lg hover:shadow-purple-500/30 transition-all"
              aria-label="התחל תרגול אדפטיבי"
            >
              התחל תרגול אדפטיבי
            </button>
          </section>
        ) : (
          <>
            {/* Navigation Tabs */}
            <nav className="flex gap-2 mb-6 overflow-x-auto pb-2" role="tablist" aria-label="סוגי תצוגה">
              {[
                { id: 'overview', label: 'סקירה כללית', icon: '📈' },
                { id: 'subjects', label: 'נושאים', icon: '📚' },
                { id: 'traps', label: 'מלכודות', icon: '⚠️' },
                { id: 'history', label: 'היסטוריה', icon: '📅' }
              ].map(tab => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`panel-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                    activeTab === tab.id
                      ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                      : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  <span aria-hidden="true">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div id="panel-overview" role="tabpanel" aria-labelledby="tab-overview" className="space-y-6">
                {/* Readiness Score Card */}
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6" aria-labelledby="readiness-title">
                  <div className="lg:col-span-2 bg-slate-800/50 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-slate-700/50">
                    <h2 id="readiness-title" className="text-slate-400 text-sm mb-3">ציון מוכנות לבחינה</h2>
                    <div className="flex items-end gap-4 mb-4">
                      <span 
                        className={`text-5xl sm:text-7xl font-black ${
                          readiness?.readiness_score >= 82 ? 'text-green-400' : 
                          readiness?.readiness_score >= 60 ? 'text-yellow-400' : 'text-red-400'
                        }`}
                        aria-label={`ציון מוכנות: ${readiness?.readiness_score?.toFixed(0) || 0} מתוך 100`}
                      >
                        {readiness?.readiness_score?.toFixed(0) || 0}
                      </span>
                      <span className="text-xl sm:text-2xl text-slate-500 mb-2">/ 100</span>
                    </div>
                    
                    {/* Progress Bar */}
                    <div 
                      className="bg-slate-700 rounded-full h-4 overflow-hidden mb-4"
                      role="progressbar"
                      aria-valuenow={readiness?.readiness_score || 0}
                      aria-valuemin="0"
                      aria-valuemax="100"
                      aria-label="התקדמות ציון מוכנות"
                    >
                      <div
                        className={`h-full rounded-full transition-all duration-1000 ${
                          readiness?.readiness_score >= 82 ? 'bg-gradient-to-r from-green-500 to-emerald-400' : 
                          readiness?.readiness_score >= 60 ? 'bg-gradient-to-r from-yellow-500 to-amber-400' : 
                          'bg-gradient-to-r from-red-500 to-rose-400'
                        }`}
                        style={{ width: `${readiness?.readiness_score || 0}%` }}
                      />
                    </div>
                    
                    {/* Status */}
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                      readiness?.is_ready ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      <span aria-hidden="true">{readiness?.is_ready ? '✅' : '⚠️'}</span>
                      <span>{readiness?.is_ready ? 'מוכן לבחינה!' : 'המשך לתרגל'}</span>
                    </div>
                  </div>

                  {/* Pass Probability */}
                  <div className="bg-slate-800/50 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-slate-700/50 flex flex-col items-center justify-center">
                    <h3 className="text-slate-400 text-sm mb-4">סיכוי מעבר</h3>
                    <div className="relative">
                      {/* Circular Progress */}
                      <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100" aria-hidden="true">
                        <circle
                          cx="50"
                          cy="50"
                          r="45"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="8"
                          className="text-slate-700"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="45"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="8"
                          strokeLinecap="round"
                          strokeDasharray={`${(readiness?.pass_probability || 0) * 2.83} 283`}
                          className={readiness?.pass_probability >= 60 ? 'text-green-500' : 'text-red-500'}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span 
                          className={`text-3xl font-black ${readiness?.pass_probability >= 60 ? 'text-green-400' : 'text-red-400'}`}
                          aria-label={`סיכוי מעבר: ${readiness?.pass_probability?.toFixed(0) || 0} אחוז`}
                        >
                          {readiness?.pass_probability?.toFixed(0) || 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Stats Grid */}
                <section aria-labelledby="stats-title">
                  <h2 id="stats-title" className="sr-only">סטטיסטיקות ביצועים</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <StatCard 
                      icon="📝" 
                      value={performance.total_questions_answered} 
                      label="שאלות נפתרו"
                      color="text-white"
                    />
                    <StatCard 
                      icon="✓" 
                      value={`${performance.overall_accuracy?.toFixed(1)}%`} 
                      label="דיוק כללי"
                      color="text-green-400"
                    />
                    <StatCard 
                      icon="🔥" 
                      value={performance.max_streak} 
                      label="רצף שיא"
                      color="text-yellow-400"
                    />
                    <StatCard 
                      icon="⚡" 
                      value={performance.current_level} 
                      label="רמה נוכחית"
                      color="text-purple-400"
                      sublabel={`מתוך 4`}
                    />
                  </div>
                </section>

                {/* Recommendations */}
                {readiness?.recommendations && readiness.recommendations.length > 0 && (
                  <section 
                    className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-2xl p-6"
                    aria-labelledby="recommendations-title"
                  >
                    <h3 id="recommendations-title" className="text-lg font-bold text-amber-400 mb-4 flex items-center gap-2">
                      <span aria-hidden="true">💡</span>
                      המלצות לשיפור
                    </h3>
                    <ul className="space-y-3" role="list">
                      {readiness.recommendations.map((rec, i) => (
                        <li key={i} className="text-slate-300 flex items-start gap-3">
                          <span className="text-amber-400 mt-1" aria-hidden="true">•</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            )}

            {/* Subjects Tab - Heat Map */}
            {activeTab === 'subjects' && (
              <div id="panel-subjects" role="tabpanel" aria-labelledby="tab-subjects" className="space-y-6">
                <section aria-labelledby="heatmap-title">
                  <h2 id="heatmap-title" className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <span aria-hidden="true">🗺️</span>
                    מפת חום - ביצועים לפי נושא
                  </h2>
                  
                  {/* Heat Map Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {subjectStats.length > 0 ? subjectStats.map((subject, index) => (
                      <div
                        key={subject.name}
                        className={`relative p-5 rounded-2xl border transition-all hover:scale-105 focus-within:ring-2 focus-within:ring-purple-500 ${getAccuracyBg(subject.accuracy)}`}
                        tabIndex={0}
                        role="article"
                        aria-label={`${getSubjectHebrew(subject.name)}: ${subject.accuracy.toFixed(0)}% דיוק מתוך ${subject.total} שאלות`}
                      >
                        <div className="text-2xl mb-2" aria-hidden="true">{getSubjectIcon(subject.name)}</div>
                        <h3 className="font-bold text-white text-sm mb-1 truncate">
                          {getSubjectHebrew(subject.name)}
                        </h3>
                        <div className="flex items-baseline gap-1">
                          <span className={`text-2xl font-black ${
                            subject.accuracy >= 80 ? 'text-green-400' :
                            subject.accuracy >= 60 ? 'text-yellow-400' :
                            subject.accuracy >= 40 ? 'text-orange-400' :
                            'text-red-400'
                          }`}>
                            {subject.accuracy.toFixed(0)}%
                          </span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          {subject.total} שאלות
                        </div>
                        
                        {/* Mini progress bar */}
                        <div className="mt-3 bg-slate-700/50 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${getAccuracyColor(subject.accuracy)}`}
                            style={{ width: `${subject.accuracy}%` }}
                          />
                        </div>
                      </div>
                    )) : (
                      <div className="col-span-full text-center py-12">
                        <p className="text-slate-400">אין נתונים עדיין. התחל לתרגל!</p>
                      </div>
                    )}
                  </div>
                </section>

                {/* Subject List */}
                <section aria-labelledby="subjects-list-title">
                  <h2 id="subjects-list-title" className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <span aria-hidden="true">📊</span>
                    פירוט מלא
                  </h2>
                  <div className="bg-slate-800/50 rounded-2xl overflow-hidden border border-slate-700/50">
                    <table className="w-full" role="table">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-right text-slate-400 text-sm font-medium px-4 py-3">נושא</th>
                          <th className="text-center text-slate-400 text-sm font-medium px-4 py-3">שאלות</th>
                          <th className="text-center text-slate-400 text-sm font-medium px-4 py-3">דיוק</th>
                          <th className="text-center text-slate-400 text-sm font-medium px-4 py-3 hidden sm:table-cell">התקדמות</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subjectStats.map((subject, index) => (
                          <tr 
                            key={subject.name}
                            className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                          >
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <span className="text-xl" aria-hidden="true">{getSubjectIcon(subject.name)}</span>
                                <span className="text-white font-medium">{getSubjectHebrew(subject.name)}</span>
                              </div>
                            </td>
                            <td className="text-center text-slate-300 px-4 py-4">{subject.total}</td>
                            <td className="text-center px-4 py-4">
                              <span className={`font-bold ${
                                subject.accuracy >= 80 ? 'text-green-400' :
                                subject.accuracy >= 60 ? 'text-yellow-400' :
                                'text-red-400'
                              }`}>
                                {subject.accuracy.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-4 py-4 hidden sm:table-cell">
                              <div className="w-full bg-slate-700 rounded-full h-2">
                                <div
                                  className={`h-full rounded-full ${getAccuracyColor(subject.accuracy)}`}
                                  style={{ width: `${subject.accuracy}%` }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>
            )}

            {/* Traps Tab */}
            {activeTab === 'traps' && (
              <div id="panel-traps" role="tabpanel" aria-labelledby="tab-traps" className="space-y-6">
                <section aria-labelledby="traps-title">
                  <h2 id="traps-title" className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <span aria-hidden="true">⚠️</span>
                    ניתוח מלכודות
                  </h2>
                  
                  {performance?.trap_analytics && Object.keys(performance.trap_analytics).length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(performance.trap_analytics)
                        .sort((a, b) => b[1].rate - a[1].rate)
                        .map(([trap, data]) => (
                          <div
                            key={trap}
                            className={`p-5 rounded-2xl border ${
                              data.rate > 50 ? 'bg-red-500/10 border-red-500/30' :
                              data.rate > 30 ? 'bg-yellow-500/10 border-yellow-500/30' :
                              'bg-green-500/10 border-green-500/30'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-white font-medium truncate">{trap}</span>
                              <span className={`text-xl font-bold ${
                                data.rate > 50 ? 'text-red-400' :
                                data.rate > 30 ? 'text-yellow-400' :
                                'text-green-400'
                              }`}>
                                {data.rate.toFixed(0)}%
                              </span>
                            </div>
                            <div className="text-sm text-slate-400">
                              {data.failures} כישלונות מתוך {data.encounters} מפגשים
                            </div>
                            <div className="mt-3 bg-slate-700/50 rounded-full h-2 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  data.rate > 50 ? 'bg-red-500' :
                                  data.rate > 30 ? 'bg-yellow-500' :
                                  'bg-green-500'
                                }`}
                                style={{ width: `${data.rate}%` }}
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-slate-800/50 rounded-2xl">
                      <span className="text-4xl mb-4 block" aria-hidden="true">🎯</span>
                      <p className="text-slate-400">עדיין אין נתוני מלכודות. המשך לתרגל!</p>
                    </div>
                  )}
                </section>
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div id="panel-history" role="tabpanel" aria-labelledby="tab-history">
                <section className="bg-slate-800/50 rounded-2xl p-6 text-center">
                  <span className="text-4xl mb-4 block" aria-hidden="true">📅</span>
                  <p className="text-slate-400">היסטוריית תרגולים תהיה זמינה בקרוב</p>
                </section>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
              <button
                onClick={() => onStartExam('diagnostic')}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-blue-500/30 transition-all focus:outline-none focus:ring-4 focus:ring-blue-500/50"
                aria-label="התחל מבחן אבחוני של 100 שאלות"
              >
                <span aria-hidden="true">🔬</span>
                מבחן אבחוני
              </button>
              <button
                onClick={() => onStartExam('adaptive')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-purple-500/30 transition-all focus:outline-none focus:ring-4 focus:ring-purple-500/50"
                aria-label="התחל תרגול אדפטיבי"
              >
                <span aria-hidden="true">🧠</span>
                תרגול אדפטיבי
              </button>
              <button
                onClick={() => onStartExam('simulator')}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-amber-500/30 transition-all focus:outline-none focus:ring-4 focus:ring-amber-500/50"
                aria-label="התחל סימולטור מלא של 100 שאלות"
              >
                <span aria-hidden="true">📝</span>
                סימולטור מלא
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

// ==================== STAT CARD COMPONENT ====================
const StatCard = ({ icon, value, label, color, sublabel }) => (
  <div 
    className="bg-slate-800/50 backdrop-blur-lg p-5 rounded-2xl border border-slate-700/50 transition-all hover:border-slate-600"
    role="article"
    aria-label={`${label}: ${value}`}
  >
    <div className="text-2xl mb-2" aria-hidden="true">{icon}</div>
    <div className={`text-2xl sm:text-3xl font-black ${color}`}>{value}</div>
    <div className="text-slate-400 text-sm">{label}</div>
    {sublabel && <div className="text-slate-500 text-xs">{sublabel}</div>}
  </div>
);

// ==================== HELPER FUNCTIONS ====================
const getSubjectHebrew = (name) => {
  const names = {
    'civil-procedure': 'סדר דין אזרחי',
    'contracts': 'דיני חוזים',
    'torts': 'דיני נזיקין',
    'property': 'דיני קניין',
    'evidence_law': 'דיני ראיות',
    'legal_ethics': 'אתיקה מקצועית',
    'administrative_law': 'משפט מנהלי',
    'criminal_law': 'דיני עונשין',
    'labor_law': 'דיני עבודה',
    'corporations_law': 'דיני חברות'
  };
  return names[name] || name;
};

const getSubjectIcon = (name) => {
  const icons = {
    'civil-procedure': '⚖️',
    'contracts': '📜',
    'torts': '🔨',
    'property': '🏠',
    'evidence_law': '🔍',
    'legal_ethics': '🎓',
    'administrative_law': '🏛️',
    'criminal_law': '⚔️',
    'labor_law': '👷',
    'corporations_law': '🏢'
  };
  return icons[name] || '📚';
};

export default EnhancedDashboard;
